import { i18n, type MessageDescriptor } from '@lingui/core';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { ApiError } from '@/shared/http';

import type { Notifier } from './notifier';

/** Texto já traduzido (`t`) ou descritor (`msg`), resolvido na hora de mostrar. */
export type Text = string | MessageDescriptor;

export interface FeedbackMessage {
  title: Text;
  description?: Text;
}

export const resolveText = (text: Text): string => (typeof text === 'string' ? text : i18n._(text));

/** Toast de erro com a regra do legado (`handleOnError`); cancelamento não mostra nada. */
export function notifyApiError(notifier: Notifier, error: unknown, defaultTitle?: Text) {
  const apiError = ApiError.from(error);
  if (apiError.isCanceled) return;
  const feedback = apiError.feedback(defaultTitle === undefined ? undefined : resolveText(defaultTitle));
  notifier.notify({ tone: feedback.tone, title: feedback.title, description: feedback.description });
}

export function notifySuccess(notifier: Notifier, message: FeedbackMessage) {
  notifier.notify({
    tone: 'success',
    title: resolveText(message.title),
    ...(message.description === undefined ? {} : { description: resolveText(message.description) }),
  });
}

export interface MutationFeedbackOptions<TData, TVariables> {
  /** Toast de sucesso; `false` ou ausente não mostra. */
  success?: FeedbackMessage | ((data: TData, variables: TVariables) => FeedbackMessage) | false;
  /** `false` desliga o toast de erro (a tela trata); `title` troca o "Ops, ocorreu um erro!". */
  error?: { title?: Text } | false;
  /** Chaves invalidadas depois do sucesso (prefixo: `keys.all` limpa o módulo). */
  invalidate?: readonly QueryKey[] | ((data: TData, variables: TVariables) => readonly QueryKey[]);
  onSuccess?: (data: TData, variables: TVariables) => unknown;
  onError?: (error: ApiError, variables: TVariables) => unknown;
}

/** Os callbacks de `useApiMutation`, separados para teste sem React. */
export function createMutationFeedback<TData, TVariables>(
  notifier: Notifier,
  queryClient: QueryClient,
  options: MutationFeedbackOptions<TData, TVariables>,
) {
  return {
    async onSuccess(data: TData, variables: TVariables) {
      if (options.success) {
        const message = typeof options.success === 'function' ? options.success(data, variables) : options.success;
        notifySuccess(notifier, message);
      }
      const keys = typeof options.invalidate === 'function' ? options.invalidate(data, variables) : (options.invalidate ?? []);
      await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      await options.onSuccess?.(data, variables);
    },
    onError(error: unknown, variables: TVariables) {
      const apiError = ApiError.from(error);
      if (options.error !== false) notifyApiError(notifier, apiError, options.error?.title);
      if (!apiError.isCanceled) options.onError?.(apiError, variables);
    },
  };
}
